/**
 * Cannot use a namespace here because Typescript namespaces don't support re-exporting for some reason
 */
import { isServerSide } from 'src/utils/validators/isServerSide';
import { RuntimeConfigurationServiceDomain } from './domain';
import { ClientAvailableEnvironmentVariables } from './RuntimeConfigurationService.client';

export { RuntimeConfigurationServiceDomain as Domain };

const SERVICE_NAME = 'RuntimeConfigurationService';
export class ConfigProvider extends RuntimeConfigurationServiceDomain.RuntimeConfigProvider {
    private static instance: ConfigProvider;

    private constructor() {
        super();
    }

    public static getInstance() {
        if (!this.instance) {
            this.instance = new ConfigProvider();
        }
        return this.instance;
    }

    /**
     * Returns a lookup of the client side environment variables to their values so we can pass them to the client
     */
    public exportClientSideEnvironmentVariables(): ClientAvailableEnvironmentVariables {
        const clientSideEnvironmentVariables = Object.values(
            RuntimeConfigurationServiceDomain.ClientAvailableConfigurationValue
        ).reduce<Record<RuntimeConfigurationServiceDomain.ClientAvailableConfigurationValue, string | null>>(
            (acc, value) => ({
                ...acc,
                [value]: this.getServerSideEnvironmentVariable(value),
            }),
            {
                [RuntimeConfigurationServiceDomain.ClientAvailableConfigurationValue.DEPLOYMENT_ENVIRONMENT]: null,
                [RuntimeConfigurationServiceDomain.ClientAvailableConfigurationValue.NODE_ENVIRONMENT]: null,
                [RuntimeConfigurationServiceDomain.ClientAvailableConfigurationValue.DATADOG_ENVIRONMENT]: null,
                [RuntimeConfigurationServiceDomain.ClientAvailableConfigurationValue.DATADOG_VERSION]: null,
            }
        );

        return {
            ...clientSideEnvironmentVariables,
            isMergeRequest: this.isMergeRequest,
        };
    }

    public override get nodeEnvironment(): RuntimeConfigurationServiceDomain.NodeEnvironment {
        const environment = process.env.NODE_ENV;
        return environment as RuntimeConfigurationServiceDomain.NodeEnvironment;
    }

    public override get isMergeRequest(): boolean {
        return !!process.env.IS_MERGE_REQUEST;
    }

    public get isStorybook(): boolean {
        return !!process.env.STORYBOOK;
    }

    public override get deploymentEnvironment(): RuntimeConfigurationServiceDomain.DeploymentEnvironment {
        const isProductionMode = this.nodeEnvironment === RuntimeConfigurationServiceDomain.NodeEnvironment.PRODUCTION;
        const env = this.getServerSideEnvironmentVariable(
            RuntimeConfigurationServiceDomain.ClientAvailableConfigurationValue.DEPLOYMENT_ENVIRONMENT
        );
        if (this.isDeploymentEnvironment(env)) {
            return env;
        }
        if (!isProductionMode) {
            return RuntimeConfigurationServiceDomain.DeploymentEnvironment.LOCAL;
        }
        return RuntimeConfigurationServiceDomain.DeploymentEnvironment.PRODUCTION;
    }

    public get pageFrameAPIKey(): string {
        return (
            this.getServerSideEnvironmentVariable(
                RuntimeConfigurationServiceDomain.ServerSideConfigurationValue.PAGE_FRAME_API_KEY
            ) ?? 'unset'
        );
    }

    public getServerSideEnvironmentVariable(
        key:
            | RuntimeConfigurationServiceDomain.ServerSideConfigurationValue
            | RuntimeConfigurationServiceDomain.ClientAvailableConfigurationValue
    ) {
        if (!isServerSide()) {
            throw new Error(`[${SERVICE_NAME}] ${ConfigProvider.name} is not available on the client side`);
        }
        return this._getServerSideEnvironmentVariable(key);
    }

    private _getServerSideEnvironmentVariable(
        key:
            | RuntimeConfigurationServiceDomain.ServerSideConfigurationValue
            | RuntimeConfigurationServiceDomain.ClientAvailableConfigurationValue
    ): string | null {
        const value = process.env[key];
        if (!value) {
            return null;
        }
        return value;
    }

    private isDeploymentEnvironment(
        env: string | null
    ): env is RuntimeConfigurationServiceDomain.DeploymentEnvironment {
        return Object.values(RuntimeConfigurationServiceDomain.DeploymentEnvironment).includes(
            env as RuntimeConfigurationServiceDomain.DeploymentEnvironment
        );
    }
}
